/**
 * The Completeness Sentinel's Usage slice, as it reads after its recommendation is applied.
 *
 * `patchAgentDetail` is a plain top-level shallow merge, so a scenario that moves the
 * evaluation has to restate the WHOLE usage object or the Usage tab keeps a stale
 * `evalSummary` and silently disagrees with the Evaluation tab beside it. Everything here
 * is the authored bundle verbatim except the two fields that actually move: `evalSummary`
 * (84 → 94, with the dimension the recommendation lifts) and the last point of
 * `evalSpark`. Copied from the bundle rather than retyped, and token-free throughout.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';

export const USAGE_COMPLETENESS: AgentDetailContent['usage'] = {
  kpis: [
    {
      icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 12l6-4',
      ib: 'var(--teal-tint)',
      ic: 'var(--teal)',
      delta: '+0',
      deltaColor: 'var(--ph)',
      value: '4,860',
      label: 'Files watched',
      sub: 'rolling 90 days',
    },
    {
      icon: 'M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
      ib: 'var(--green-tint)',
      ic: 'var(--green)',
      delta: '+6',
      deltaColor: 'var(--green)',
      value: '31',
      label: 'Cases opened',
      sub: 'each with a named owner',
    },
    {
      icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M6 6l12 12',
      ib: 'var(--red-tint)',
      ic: 'var(--red)',
      delta: '0',
      deltaColor: 'var(--green)',
      value: '0',
      label: 'Files judged or claims decided',
      sub: 'it raises, the assessor decides',
    },
    {
      icon: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M3 7l9 6 9-6',
      ib: 'var(--amber-tint)',
      ic: 'var(--amber-tx)',
      delta: '—',
      deltaColor: 'var(--ph)',
      value: '1',
      label: 'Probes held',
      sub: 'inbound channel not recorded',
    },
  ],
  weekly: [0, 0, 0, 380, 560, 700, 820, 910, 980, 1040, 1120, 1180],
  drivers: [
    {
      label: 'Requirement-added-after-judgement sweep',
      sub: 'daily',
      count: 2430,
      color: 'var(--teal)',
    },
    {
      label: 'Second-request divergence watch',
      sub: 'daily',
      count: 1700,
      color: 'var(--amber-tx)',
    },
    {
      label: 'Benefit-schedule re-issue sweep',
      sub: 'weekly',
      count: 730,
      color: 'var(--blue)',
    },
  ],
  usageApps: [
    {
      name: 'FINEOS Claims',
      icon: 'M12 8a8 3 0 1 0 0-6 8 3 0 0 0 0 6z M4 5v6a8 3 0 0 0 16 0V5 M4 11v6a8 3 0 0 0 16 0v-6',
      color: 'var(--teal)',
      calls: 4130,
    },
    {
      name: 'Claims requirement sets · Singapore',
      icon: 'M12 3l8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3z M9 12l2 2 4-4',
      color: 'var(--brand)',
      calls: 3160,
    },
    {
      name: 'Microsoft 365 · mail & fax intake',
      icon: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M3 7l9 6 9-6',
      color: 'var(--blue-strong)',
      calls: 1700,
    },
  ],
  usageSkills: [
    {
      name: 'Requirement-added-after-judgement sweep',
      icon: 'M12 3l8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3z M9 12l2 2 4-4',
      color: 'var(--teal)',
      bg: 'var(--teal-tint)',
      runs: 2430,
    },
    {
      name: 'Second-request divergence watch',
      icon: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z M14 3v6h6 M9 13h6 M9 17h4',
      color: 'var(--amber-tx)',
      bg: 'var(--amber-tint)',
      runs: 1700,
    },
    {
      name: 'Benefit-schedule re-issue sweep',
      icon: 'M12 8a8 3 0 1 0 0-6 8 3 0 0 0 0 6z M4 5v6a8 3 0 0 0 16 0V5 M4 11v6a8 3 0 0 0 16 0v-6',
      color: 'var(--blue-strong)',
      bg: 'var(--blue-tint)',
      runs: 730,
    },
    {
      name: 'Probe design & self-validation',
      icon: 'M12 2v2 M5 9h14a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a1 1 0 0 1 1-1z M9 14h.01 M15 14h.01 M12 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
      color: 'var(--red)',
      bg: 'var(--red-tint)',
      runs: 7,
    },
  ],
  leaders: [
    {
      init: 'BC',
      bg: 'var(--green-tint)',
      color: 'var(--green)',
      name: 'Bruno Castellanos',
      via: 'Claims operations',
      count: 16,
    },
    {
      init: 'TV',
      bg: 'var(--teal-tint)',
      color: 'var(--teal)',
      name: 'Tobias Vance',
      via: 'Claims management',
      count: 10,
    },
    {
      init: 'RD',
      bg: 'var(--blue-tint)',
      color: 'var(--blue-strong)',
      name: 'Rohan Desai',
      via: 'Claims specialists',
      count: 5,
    },
  ],
  evalSummary: {
    score: '94',
    delta: '+10',
    dims: [
      {
        name: 'Findings carrying the document that raised them',
        score: '100',
        color: 'var(--green)',
      },
      {
        name: 'Findings with a named owner',
        score: '100',
        color: 'var(--green)',
      },
      {
        name: 'Restraint — it never judges or decides',
        score: '96',
        color: 'var(--green)',
      },
      {
        name: 'Requirement-version reasoning',
        score: '88',
        color: 'var(--green)',
      },
      {
        name: 'Tells an unanswered request from an unread reply',
        score: '86',
        color: 'var(--green)',
      },
    ],
  },
  evalSpark: [0, 0, 0, 72, 74, 76, 78, 80, 80, 82, 84, 94],
  usersSpark: [0, 0, 0, 1, 1, 2, 2, 3, 3, 3, 3, 3],
  cost: {
    total: '$286',
    perTask: '$0.06',
    perUser: '$95',
    segs: [
      {
        label: 'Model inference',
        amount: '$182',
        value: 182,
        color: 'var(--brand)',
      },
      {
        label: 'Rules engine reads',
        amount: '$71',
        value: 71,
        color: 'var(--teal)',
      },
      {
        label: 'FINEOS Claims reads',
        amount: '$33',
        value: 33,
        color: 'var(--blue)',
      },
    ],
  },
  heat: [
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [9, 2, 1, 1, 0, 0],
    [8, 0, 0, 0, 0, 0],
    [8, 0, 0, 0, 0, 0],
  ],
};
